import { useEffect, useState, useRef } from 'react'
import { sendHapticFeedback } from '../helpers/Mobile'

const useBreathingAnimationFrame = (practiceMinutes, breathingData) => {
  // Create a reference to our animation frame
  const animationFrameRef = useRef()
  const animationStateRef = useRef()
  const startTimeRef = useRef()

  const practiceSeconds = practiceMinutes * 60

  const [isFinished, setIsFinished] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(practiceSeconds)
  const [minutesRemaining, setMinutesRemaining] = useState(practiceMinutes)
  const [countdown, setCountdown] = useState(0)

  // Set up our animation states
  const singleCycleStates = [
    {
      className: 'inhale',
      message: 'Breathe in',
      duration: breathingData?.inhale_duration
    },
    {
      className: 'inhale hold',
      message: 'Hold',
      duration: breathingData?.inhale_hold_duration
    },
    {
      className: 'exhale',
      message: 'Breathe out',
      duration: breathingData?.exhale_duration
    },
    {
      className: 'exhale hold',
      message: 'Hold',
      duration: breathingData?.exhale_hold_duration
    }
  ]

  // If the practice has two cycles, add the second cycle to the states
  const doubleCycleStates = [
    ...singleCycleStates,
    {
      className: 'inhale',
      message: 'Breathe in',
      duration: breathingData?.second_inhale_duration
    },
    {
      className: 'inhale hold',
      message: 'Hold',
      duration: breathingData?.second_inhale_hold_duration
    },
    {
      className: 'exhale',
      message: 'Breathe out',
      duration: breathingData?.second_exhale_duration
    },
    {
      className: 'exhale hold',
      message: 'Hold',
      duration: breathingData?.second_exhale_hold_duration
    }
  ]

  // Does this practice have one cycle or two?
  const cycles = breathingData?.second_inhale_duration ? 2 : 1

  // Set the total states based on the number of cycles
  const animationStates = useRef(
    cycles === 1 ? singleCycleStates : doubleCycleStates
  )

  // Let's get the total cycle time, so we can determine our animation states
  const totalCycleTime = animationStates.current.reduce(
    (acc, state) => acc + (state.duration || 0),
    0
  )

  /**
   * Helper function to determine which state index to use, based on the seconds passed in.
   * It loops through the states and adds the duration of each state to a total and compares
   * that total to the seconds passed in. If the total is greater than the seconds passed in,
   * we know we're in that state.
   * @param {number} seconds - The number of seconds passed in
   * @param {array} states - The array of states
   * @returns {number} stateIndex - The index of the state we're in
   */
  const getAnimationStateIndexForTime = (seconds, states) => {
    let total = 0
    let stateIndex = 0

    for (let i = 0; i < states.length; i++) {
      total += states[i].duration

      if (total > seconds) {
        stateIndex = i
        break
      }
    }

    return stateIndex
  }

  /**
   * Helper function to determine how many seconds remain in the countdown, based on the current
   * state index and the seconds passed in.
   * @param {number} stateIndex - The index of the state we're in
   * @param {number} animationCycleSeconds - The number of seconds in the current cycle
   * @param {array} states - The array of states
   * @returns {number} secondsRemaining - The number of seconds remaining in the countdown
   */
  const getCountdownSecondsForState = (
    stateIndex,
    animationCycleSeconds,
    states
  ) => {
    let total = 0

    for (let i = 0; i < stateIndex; i++) {
      total += states[i].duration
    }

    return animationCycleSeconds - total
  }

  const handleComplete = () => {
    // Cancel the animation frame
    cancelAnimationFrame(animationFrameRef.current)
    // setAnimationState(null)
    setSecondsRemaining(0)
    setMinutesRemaining(0)
    setIsFinished(true)
  }

  const handleStateChange = state => {
    // Send a haptic feedback
    sendHapticFeedback()
  }

  // Start the animation
  useEffect(() => {
    // Return early if we don't have any animation states
    if (!animationStates.current?.length || !practiceSeconds || !totalCycleTime)
      return

    /**
     * Animation function that runs every frame
     * @param {time} time
     */
    const animate = time => {
      // If we don't have a start time, set it
      if (startTimeRef.current === undefined) startTimeRef.current = time

      const delta = time - startTimeRef.current ?? 0

      // Get our seconds and minutes
      const seconds = Math.floor(delta / 1000)

      // Determine which animation state we should be in, based on the time
      const animationCycleSeconds = seconds % totalCycleTime || 0

      // Now we need to determine which animation state we should be in, by looking at the animation cycle index
      // and checking the duration of each animation state in the cycle
      const animationStateIndex = getAnimationStateIndexForTime(
        animationCycleSeconds,
        animationStates?.current
      )
      const currentAnimationState =
        animationStates?.current[animationStateIndex]

      // Let's create a countdown timer, based on the duration of the current animation state
      // Store the countdown in a separate variable that we update every second
      const countdown = getCountdownSecondsForState(
        animationStateIndex,
        animationCycleSeconds,
        animationStates.current
      )

      // Figure out how many minutes and seconds are remaining in the practice
      // and update the countdown timer
      const totalSecondsRemaining = practiceSeconds - seconds
      const minutesRemaining = Math.floor(totalSecondsRemaining / 60)
      const secondsRemaining = totalSecondsRemaining % 60

      // Do we have a state change? If so, send a haptic feedback
      if (
        currentAnimationState?.className !==
        animationStateRef?.current?.className
      )
        handleStateChange(currentAnimationState)

      // The 'state' will always be the initial value here
      animationFrameRef.current = requestAnimationFrame(animate)
      animationStateRef.current = currentAnimationState

      // If the minutes and seconds remaining are 0, we're done with the practice
      if (minutesRemaining <= 0 && secondsRemaining <= 0) {
        handleComplete()
        return
      }

      // Set our seconds and minutes and animation state
      setSecondsRemaining(secondsRemaining)
      setMinutesRemaining(minutesRemaining)
      setCountdown(currentAnimationState.duration - countdown)
    }

    animationFrameRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrameRef.current)
  }, [practiceSeconds, totalCycleTime]) // Make sure the effect runs only once

  return {
    secondsRemaining,
    minutesRemaining,
    animationState: animationStateRef.current,
    countdown,
    isFinished
  }
}

export default useBreathingAnimationFrame
